import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import {
  AccessStatus,
  LearningItemDetails,
  PlannerAccess,
} from "api/resources/learningItems/types"
import Monitor from "assets/Fallback/monitor.svg"
import Fallback from "components/Fallback"
import FullPageLoading from "components/FullPageLoading"
import PageContainer from "components/PageContainer"
import { Outlet, useOutletContext, useParams } from "react-router-dom"
import { learningItemSlugs } from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"

import LearningItemHeader from "../Header"
// import LearningPausedBanner from "./LearningPausedBanner"

const voidTab = { name: () => "", link: "" }
type ContextType = {
  course: LearningItemDetails
  showLearningPausedBanner: (state: boolean) => void
  accessStatus: AccessStatus
  planner: PlannerAccess
}

const ITEM_TYPE = "course"

const Course = () => {
  const { slug, parentSlug } = useParams() as {
    slug: string
    parentSlug?: string
  }

  const {
    pgctl: { isNewDeliveryMode },
  } = useEnrollments()

  const { data, isLoading, isError } = useQuery({
    queryFn: () =>
      api.learningItems.getLearningItemDetails({
        urlParams: {
          itemType: ITEM_TYPE,
          itemSlug: slug,
        },
      }),
    queryKey: queries.learningItems.details(ITEM_TYPE, slug).queryKey,
  })

  const planner = useQuery({
    enabled: Boolean(data),
    queryFn: () =>
      api.learningItems.getPlanner({
        urlParams: {
          itemType: ITEM_TYPE,

          itemId: data!.id,
        },
      }),
  })

  if (isLoading || planner.isLoading) return <FullPageLoading />

  if (isError || planner.isError)
    return (
      <Fallback
        data={{
          title: "Something went wrong",
          description: "Please contact care@suraasa.com",
          image: Monitor,
        }}
        className="pt-4"
      />
    )

  const getClassesTab = () => {
    if (isNewDeliveryMode) {
      return voidTab
    }
    return { name: () => "Classes", link: "classes" }
  }
  const getAssessmentsTab = () => {
    if (isNewDeliveryMode) {
      return voidTab
    }
    return { name: () => "Assessments", link: "assessments" }
  }

  const getAnalyticsTab = () => {
    if (isNewDeliveryMode && parentSlug === learningItemSlugs.pgctl) {
      return { name: () => "Analytics", link: "analytics" }
    }
    return voidTab
  }

  const tabs = [
    { name: () => "Overview", link: "overview" },
    getClassesTab(),
    { name: () => "Skill Evidences (Assignments)", link: "assignments" },
    getAssessmentsTab(),
    { name: () => "Resources", link: "resources" },
    getAnalyticsTab(),
    { name: () => "About", link: "about" },
  ].filter(x => x.link)

  return (
    <>
      {data.type === "course" && (
        <LearningItemHeader
          itemName={data.name}
          image={data.image}
          itemSlug={slug}
          data={tabs}
          className="mb-3"
          showDiscussionsButton
        />
      )}
      {/* Bottom padding for each page so content doesn't get cropped */}
      <PageContainer className="pb-10">
        <Outlet
          context={{
            course: data,
            planner: planner.data,
            accessStatus: planner.data.accessStatus,
          }}
        />
      </PageContainer>
    </>
  )
}

export default Course

export function useCourseDetails() {
  return useOutletContext<ContextType>()
}
